import { useParams } from 'react-router-dom';
import ActivityPage, { ACTIVITY_PAGE_TYPE } from '../../components/Activity';

import {
  ActivitySubmission,
  createTeamsAIReview,
  saveTeamsActivity,
  validateTeamsAnswerAttempt,
} from '../../hydra';
import { getTeamsActivity } from '../../api/teams';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../routing';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/use-auth';
import { ActivityContextQuestionAnswer } from '../../contexts/activity';
import { useCallback } from 'react';
import { useTeamsActivity } from '../../hooks/use-hydra/teams';

export default function Activity() {
  const { trackSlug, missionSlug, activitySlug, companySlug } =
    useParams<{
      trackSlug?: string;
      missionSlug: string;
      activitySlug: string;
      companySlug?: string;
    }>();
  const { getTokenSilently } = useAuth();
  const history = useHistory();

  const activityState = useTeamsActivity({
    trackSlug: trackSlug ?? '',
    missionSlug,
    activitySlug,
    teamSlug: companySlug ?? '',
  });
  const activitySessionId = activityState.value?.activity_session_id ?? -1;

  const saveSubmission = async (submission: ActivitySubmission) => {
    const token = await getTokenSilently();
    await saveTeamsActivity(token, {
      trackSlug: trackSlug ?? '',
      submission,
    });
  };

  const goToNextActivity = async (): Promise<void> => {
    const { next_activity_slug, next_mission_slug, content_slug } =
      activityState.value ?? {};

    const hasNextActivity = next_activity_slug && next_mission_slug;

    toast.info(
      hasNextActivity
        ? 'Moving on to the next activity...'
        : 'Opening the module dashboard...',
      {
        autoClose: 2000,
      }
    );
    setTimeout(() => {
      if (hasNextActivity) {
        history.push(
          ROUTES.TEAMS.ACTIVITY.getPath({
            trackSlug: trackSlug ?? '',
            missionSlug: next_mission_slug,
            activitySlug: next_activity_slug,
            teamSlug: companySlug,
          })
        );
      } else {
        history.push(
          ROUTES.TEAMS.MODULE_DASHBOARD.getPath({
            teamSlug: companySlug,
            moduleSlug: content_slug ?? '',
            trackSlug: trackSlug ?? '',
          })
        );
      }
    }, 2000);
  };

  const getActivity = async () => {
    const token = await getTokenSilently();
    return getTeamsActivity(token, {
      missionSlug,
      activitySlug,
      trackSlug: trackSlug ?? '',
    });
  };

  const createAIReview = async ({
    activitySlug,
    submissionId,
    questionSlugToAnswer,
  }: {
    activitySlug: string;
    submissionId: number;
    questionSlugToAnswer: Record<string, ActivityContextQuestionAnswer>;
  }) => {
    const token = await getTokenSilently();
    if (!submissionId) {
      throw new Error('No submission found');
    }
    return createTeamsAIReview(token, {
      activitySlug,
      submissionId,
      questionSlugToAnswer,
    });
  };

  const submitAnswer = useCallback(
    async ({
      questionSlug,
      value,
    }: {
      questionSlug: string;
      value: string;
    }) => {
      const token = await getTokenSilently();

      await validateTeamsAnswerAttempt(token, {
        value,
        activitySessionId,
        questionSlug,
      });
    },
    [getTokenSilently, activitySessionId]
  );

  return (
    <ActivityPage
      activityState={activityState}
      dashboardUrl={ROUTES.TEAMS.MODULE_DASHBOARD.getPath({
        teamSlug: companySlug,
        moduleSlug: activityState.value?.content_slug ?? '',
        trackSlug: trackSlug ?? '',
      })}
      goToNextActivity={goToNextActivity}
      saveSubmission={saveSubmission}
      type={ACTIVITY_PAGE_TYPE.TEAMS}
      getActivity={getActivity}
      createAIReview={createAIReview}
      submitAnswer={submitAnswer}
    />
  );
}
